import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import { Api } from '../../api';
import { usePaginatedProvider } from '../../state/paginatedProviderStore';
import { VMPaginatedGrid } from '../../components/main/VMPaginatedGrid';
import StarIcon from '@mui/icons-material/Star';
import { ListItem, Typography } from '@mui/material';
import { gridColumn } from 'utils/gridUtils';
import { GridRowParams } from '@material-ui/data-grid';
import { Box } from '@mui/material';
import { ImageGalleryModal } from './ImageGalleryModal';
import ActionsMenu from 'components/other/ActionsMenu';
import { drawerStore } from 'state/drawerStore';
import { FormInputTypes } from 'utils/constants';
import credentialStore from 'state/credentialStore';
import { useProvider } from 'state/providerStore';

export const LocationDetailsReviews = observer(() => {
  const { locationid } = useParams<{ locationid: string }>();
  const [open, setOpen] = useState(false);
  const [modalImages, setModalImages] = useState<string[]>([]);

  const locationReviewsSummaryProvider = usePaginatedProvider(() =>
    Api.review.getReviewSummary(locationid),
  );

  const locationReviewsProvider = usePaginatedProvider((offset, limit) =>
    Api.review.listAppReviews({
      offset,
      limit,
      targetType: 'location',
      targetId: locationid,
    }),
  );

  const locationProvider = useProvider(() => Api.charge.getLocation(locationid));

  const reviewFormInputs = [
    { name: 'rating', label: 'What is your rate?', type: FormInputTypes.starRating },
    { name: 'images', label: 'Add Photo', type: FormInputTypes.imageUpload },
    { name: 'text', label: 'Type text here...', type: FormInputTypes.multilineTextField },
  ];

  const onClickConfirmAddReview = async () => {
    const { rating, images, text } = drawerStore.values;
    await Api.review.createAdminReview({
      rating,
      images,
      text,
      targetType: 'location',
      targetId: locationid,
      userName: `${credentialStore.credentials.user.name}  ${credentialStore.credentials.user.familyName}`,
      userEmail: credentialStore.credentials.user.email,
    });

    locationReviewsProvider.reloadResource();
    locationReviewsSummaryProvider.reloadResource();
  };

  const onClickAddReview = () => {
    drawerStore.openDrawer({
      label: 'Add Review',
      inputs: reviewFormInputs,
      clickCallback: onClickConfirmAddReview,
      buttonLabel: 'Save',
      showButton: true,
    });

    drawerStore.setValues({
      rating: 0,
      images: [],
      text: '',
    });
  };

  const confirmEditReview = async (review) => {
    const { rating, images, text } = drawerStore.values;

    await Api.review.updateAppReview(review.id, {
      rating,
      images,
      text,
    });

    locationReviewsProvider.reloadResource();
    locationReviewsSummaryProvider.reloadResource();
  };

  const onClickEditReview = (review) => {
    drawerStore.openDrawer({
      label: 'Edit Review',
      inputs: reviewFormInputs,
      clickCallback: () => confirmEditReview(review),
      buttonLabel: 'Save',
      showButton: true,
    });

    drawerStore.setValues({
      rating: review.rating,
      images: review.reviewImages || [],
      text: review.text || '',
    });
  };

  const onClickDeleteReview = async (review) => {
    await Api.review.deleteAdminReview(review.id);
    locationReviewsProvider.reloadResource();
    locationReviewsSummaryProvider.reloadResource();
  };

  const reviewActions = (params: GridRowParams) => (
    <ActionsMenu>
      <ListItem button onClick={() => onClickEditReview(params.row)}>
        <Typography style={{ width: '100%' }}>Edit</Typography>
      </ListItem>
      <ListItem button onClick={() => onClickDeleteReview(params.row)}>
        <Typography style={{ width: '100%' }}>Delete</Typography>
      </ListItem>
    </ActionsMenu>
  );

  const reviewsColumns = () => [
    gridColumn('review', {
      flex: 4,
      sortableFields: locationReviewsProvider.sortable,
      minWidth: 150,
      renderCell: (params: GridRowParams) => (
        <Typography
          variant="body2"
          style={{
            width: '100%',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
        >
          {params.row.text || ''}
        </Typography>
      ),
    }),

    gridColumn('images', {
      flex: 2,
      minWidth: 150,
      renderCell: (params: GridRowParams) => {
        const reviewImages = params.row.reviewImages || [];
        const displayImages = reviewImages.slice(0, 3);
        if (displayImages.length === 0) return null;

        return (
          <Box display="flex" gap={1} flexWrap="wrap">
            {displayImages.map((src: string, idx: number) => (
              <img
                key={idx}
                src={src}
                alt="Review"
                style={{
                  width: 60,
                  height: 60,
                  objectFit: 'contain',
                  background: '#fff',
                  borderRadius: 8,
                  cursor: 'pointer',
                  border: '1px solid #e0e0e0',
                }}
                onClick={() => {
                  setModalImages(reviewImages);
                  setOpen(true);
                }}
              />
            ))}
          </Box>
        );
      },
    }),
    gridColumn('rating', {
      flex: 1,
      sortableFields: locationReviewsProvider.sortable,
      minWidth: 150,
      renderCell: (params: GridRowParams) => {
        const rating = params.row.rating ?? 0;
        return (
          <Box display="flex">
            {Array.from({ length: 5 }).map((_, index) => (
              <StarIcon
                key={index}
                style={{
                  color: index < rating ? '#FFD700' : '#E0E0E0',
                  fontSize: 20,
                }}
              />
            ))}
          </Box>
        );
      },
    }),
    gridColumn('date', {
      flex: 1,
      sortableFields: locationReviewsProvider.sortable,
      minWidth: 150,
      renderCell: (params: GridRowParams) => {
        const date = params.row.createdAt ? new Date(params.row.createdAt) : null;
        if (!date) return <div />;
        return (
          <Typography variant="body2">
            {date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </Typography>
        );
      },
    }),
    gridColumn('author', {
      flex: 1,
      sortableFields: locationReviewsProvider.sortable,
      minWidth: 150,
      renderCell: (params: GridRowParams) => (
        <Typography variant="body2">{params.row.userName || '-'}</Typography>
      ),
    }),
    gridColumn('', {
      width: 60,
      renderCell: (params: GridRowParams) => reviewActions(params),
      align: 'center',
      headerAlign: 'center',
    }),
  ];

  return (
    <div style={{ width: '100%' }}>
      <Box display="flex" alignItems="center" mb={2} gap={1}>
        <Typography variant="h5">
          {locationReviewsSummaryProvider.data?.averageRating?.toFixed(1) || '-'}
        </Typography>

        <Box display="flex">
          {Array.from({ length: 5 }).map((_, index) => {
            const avg = locationReviewsSummaryProvider.data?.averageRating || 0;
            return (
              <StarIcon
                key={index}
                style={{
                  color: index < Math.round(avg) ? '#FFD700' : '#E0E0E0',
                  fontSize: 37,
                  fontWeight: 600,
                }}
              />
            );
          })}
        </Box>
      </Box>
      <ImageGalleryModal open={open} onClose={() => setOpen(false)} images={modalImages} />

      <VMPaginatedGrid
        label={locationProvider.data?.name || 'Location'}
        rows={locationReviewsProvider.data}
        columns={reviewsColumns()}
        onClickAdd={onClickAddReview}
        count={locationReviewsProvider.count}
        onPageOffsetChange={locationReviewsProvider.onPageChange}
        rowHeight={80}
      />
    </div>
  );
});

export default LocationDetailsReviews;
